import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Link } from "@tanstack/react-router";
import { useId } from "react";

import { isNormalGame, useMatches } from "@/features/matches";
import { BlockError } from "@/shared/ui/block-error";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { useAnalysis } from "../../api/useAnalysis";
import { useAssetLookup } from "../../api/useAssetLookup";
import { FindingView } from "../../findings/FindingView";
import type { MatchSummary } from "../../model/Analysis";
import { selectFixFirst } from "../../model/selectFixFirst";
import { AssetIcon } from "../AssetIcon";
import { ScoreRing } from "../ScoreRing";

const LOAD_ERROR = msg({
  id: "analysis.last.load_error",
  message: "Could not load your last analysis",
});
const EYEBROW = msg({ id: "analysis.last.eyebrow", message: "Latest analyzed match" });
const HEADLINE = msg({
  id: "analysis.last.headline",
  message: "{hero} · {result} · {kills} / {deaths} / {assists}",
});
const WIN = msg({ id: "analysis.last.win", message: "victory" });
const LOSS = msg({ id: "analysis.last.loss", message: "defeat" });
const NO_FIXES = msg({
  id: "analysis.last.no_fixes",
  message: "No serious mistakes in this match.",
});
const OPEN = msg({ id: "analysis.last.open", message: "Open analysis" });

type LastAnalysisCardProps = {
  match: MatchSummary;
  analysisId: number;
};

const LastAnalysisCard = ({ match, analysisId }: LastAnalysisCardProps) => {
  const { _ } = useLingui();
  const headingId = useId();
  const hero = useAssetLookup("heroes")(match.hero_id);
  const analysis = useAnalysis(analysisId);

  if (analysis.isPending) {
    return <Skeleton className="h-52 rounded-xl" />;
  }
  if (analysis.isError) {
    return (
      <BlockError
        title={_(LOAD_ERROR)}
        retrying={analysis.isFetching}
        onRetry={() => void analysis.refetch()}
      />
    );
  }
  const fixes = selectFixFirst(analysis.data.findings);

  return (
    <section
      aria-labelledby={headingId}
      data-match-id={match.match_id}
      className="flex flex-col gap-4 rounded-xl border border-border-strong bg-surface p-5"
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3.5">
        <AssetIcon asset={hero} size={56} className="rounded-[10px]" />
        <div className="flex min-w-0 grow flex-col gap-0.5">
          <p className="text-xs tracking-[0.08em] text-accent uppercase">{_(EYEBROW)}</p>
          <h2 id={headingId} className="text-xl font-semibold">
            {_({
              ...HEADLINE,
              values: {
                hero: hero.name,
                result: _(match.won ? WIN : LOSS),
                kills: match.kills,
                deaths: match.deaths,
                assists: match.assists,
              },
            })}
          </h2>
        </div>
        {match.score !== null && <ScoreRing score={match.score} />}
      </div>
      {fixes.length === 0 ? (
        <p className="text-sm text-muted">{_(NO_FIXES)}</p>
      ) : (
        <ul className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-2.5">
          {fixes.map((finding, index) => (
            // Порядок и состав находок разбора не меняются — индекс как ключ безопасен.
            <li key={index}>
              <FindingView finding={finding} variant="compact" />
            </li>
          ))}
        </ul>
      )}
      <Button asChild className="h-11 self-start px-4.5 text-[15px] font-semibold">
        <Link to="/matches/$matchId" params={{ matchId: match.match_id }}>
          {_(OPEN)}
        </Link>
      </Button>
    </section>
  );
};

// «Последний матч разобран» на главной (макет design/prototype/Home.dc.html): последний
// матч из первой страницы истории с готовым разбором и до трёх ошибок, которые стоит исправить первыми.
// Кольцо «N из 100» — быстрая оценка матча (`MatchSummary.score`), не балл полного разбора.
// Пока грузится список матчей — заглушка на месте блока; готового разбора среди
// загруженных матчей нет — блока нет.
export const LastAnalysis = () => {
  const { query, items } = useMatches();
  if (query.isPending) {
    return <Skeleton className="h-52 rounded-xl" />;
  }
  // Разборы Street Brawl, сделанные до проверки режима, не показываем: они бессмысленны.
  const match = items.find((item) => item.analysis_status === "done" && isNormalGame(item));
  if (match === undefined || match.analysis_id === null) {
    return null;
  }
  return <LastAnalysisCard key={match.analysis_id} match={match} analysisId={match.analysis_id} />;
};
